/**
 * useChartConfig - Chart Configuration Hook
 * 
 * SOLID Principle: Single Responsibility Principle (SRP)
 * Purpose: Handles all chart configuration logic including axis setup,
 * field categorization, and configuration generation. Separates configuration
 * concerns from data processing and rendering.
 * 
 * Responsibilities:
 * - Generate field keys for chart data
 * - Categorize fields into directional vs regular
 * - Build axis configuration for merged and individual modes
 * - Calculate chart margins and layout
 * - Manage configuration state and fallbacks
 */

import React, { useState } from 'react';
import { ChartDataProcessor } from '../ChartDataProcessor.js';
import { ChartConfigBuilder } from '../ChartConfigBuilder.js';
import { FieldTypeRegistry } from '../FieldTypeRegistry.js';
import { LabelUtils } from '../LabelUtils.js';
import { ColorUtils } from '../ColorUtils.js';

export function useChartConfig({
  selectedFields,
  perBuoySelectedFields,
  selectedBuoys,
  chartData,
  mergedMode
}) {
  const [previousChartConfig, setPreviousChartConfig] = useState(null);

  // === Chart Configuration ===
  const chartConfig = React.useMemo(() => {
    // === Early Returns ===
    const hasAnyMergedFields = mergedMode
      ? Object.values(perBuoySelectedFields || {}).some(arr => (arr?.length ?? 0) > 0)
      : (selectedFields?.length ?? 0) > 0;

    if (!hasAnyMergedFields) {
      return null;
    }
    
    // Generate the field keys that will actually be in the chart data
    let actualFieldKeys;
    if (mergedMode && selectedBuoys.length > 0) {
      // In merged mode, field keys are prefixed with buoy IDs
      actualFieldKeys = [];
      selectedBuoys.forEach(buoyId => {
        const fields = perBuoySelectedFields[buoyId] || [];
        fields.forEach(field => {
          actualFieldKeys.push(`${buoyId}_${field}`);
        });
      });
    } else {
      actualFieldKeys = selectedFields;
    }
    
    // Separate field types using actual field keys
    const { directionalFields, regularFields } = ChartDataProcessor.categorizeFields(actualFieldKeys, mergedMode);
    
    // Build axis configuration for regular fields only (exclude directional fields)
    // Filter out directional fields from Y-axis creation
    const nonDirectionalFields = regularFields.filter(field => !directionalFields.includes(field));
    
    let axisConfig;
    if (mergedMode) {
      // Group non-directional fields by base field type for shared axes
      const baseFieldGroups = {};
      nonDirectionalFields.forEach(fieldKey => {
        const baseKey = fieldKey.replace(/^[A-Z0-9]+_/, '');
        if (!baseFieldGroups[baseKey]) {
          baseFieldGroups[baseKey] = [];
        }
        baseFieldGroups[baseKey].push(fieldKey);
      });
      
      // Create one axis per unique base field type
      axisConfig = Object.entries(baseFieldGroups).map(([baseKey, fieldKeys], i) => {
        // Calculate domain using all values from all buoys for this field type
        const allValues = fieldKeys.flatMap(fieldKey =>
          chartData
            .map(item => item[fieldKey])
            .filter(val => typeof val === 'number' && !isNaN(val) && isFinite(val))
        );
        
        let computedDomain;
        if (allValues.length === 0) {
          computedDomain = null;
        } else {
          const min = Math.min(...allValues);
          const max = Math.max(...allValues);
          if (min === max) {
            const padding = Math.abs(max) * 0.1 || 1;
            computedDomain = [min - padding, max + padding];
          } else {
            const range = max - min;
            const padding = range * 0.05;
            computedDomain = [min - padding, max + padding];
          }
        }
        const domain = Array.isArray(computedDomain) && computedDomain.length === 2
          ? computedDomain
          : ['auto', 'auto'];
          
        // Direct color lookup for baseKey since we already stripped buoy prefixes
        let color;
        if (baseKey === 'procv_co2:pCO2') {
          color = '#6c757d'; // Force grey for pCO2
        } else {
          color = ColorUtils.getFieldColor(baseKey);
        }
        
        return {
          baseKey,
          fieldKeys, // Array of all buoy-prefixed field keys that use this axis
          yAxisId: `y-${i}`,
          orientation: i % 2 === 0 ? 'left' : 'right',
          color: color,
          units: FieldTypeRegistry.getUnits(baseKey),
          label: LabelUtils.getAxisLabel(baseKey), // Use base key for label
          domain,
          index: i,
        };
      });
    } else {
      axisConfig = ChartConfigBuilder.buildAxisConfiguration(nonDirectionalFields, chartData);
    }
    
    // If only directional fields are selected, create a dummy axis for ReferenceDot positioning
    const effectiveAxisConfig = (axisConfig && axisConfig.length > 0) ? axisConfig : [{
      fieldKey: 'dummy',
      yAxisId: 'default-yAxis',
      orientation: 'left',
      color: '#ccc',
      units: '',
      label: '',
      domain: [0, 1],
      index: 0
    }];
    
    // Calculate chart margins based on axes
    const margins = ChartConfigBuilder.calculateChartMargins(
      effectiveAxisConfig, 
      directionalFields.length > 0
    );
    
    const config = {
      directionalFields,
      regularFields,
      axisConfig: effectiveAxisConfig,
      margins,
      colorMapping: ChartConfigBuilder.buildColorMapping([...regularFields, ...directionalFields])
    };

    // Store valid config for potential fallback
    if (config.regularFields.length > 0 || config.directionalFields.length > 0) {
      setPreviousChartConfig(config);
    }

    return config;
  }, [selectedFields?.join('|'), JSON.stringify(perBuoySelectedFields), chartData, mergedMode, selectedBuoys?.join('|')]);

  return {
    chartConfig,
    previousChartConfig
  };
}

export default useChartConfig;